import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, ChevronRight, CheckCircle, AlertTriangle, Clock, Loader2 } from 'lucide-react';
import { useTheme } from '../../../components/common/Theme/ThemeContext';
import apiClient from '../../../components/api/apiClient';
import { resolveWorkflowRunServiceBaseUrl } from '../workflowbuilder/workflowRunStartService';
import WorkflowTimeline, { type TimelineEntry } from './WorkflowTimeline';
import { WorkflowLogModalToolbar, WORKFLOW_LOG_AUTO_REFRESH_MS } from './WorkflowLogModalToolbar';

export interface LogRow {
  id: string;
  time: string;
  status: 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Cancelled';
  detail: string;
  /** From run-logs API; optional follow-up for timeline services. */
  timelineRelativePath?: string;
  durationSeconds?: number;
  /** Raw timestamp for sorting (newest first). */
  occurredAtUtc?: string;
}

interface Props {
  workflowId: string;
  name: string;
  onClose: () => void;
  /** When true, the run list "Auto" refresh toggle starts enabled (e.g. after starting a test run). */
  initialAutoRefreshList?: boolean;
  /**
   * When true with initialAutoRefreshList, timeline Auto starts on instead of the run list.
   * After the first successful load, opens the timeline for the newest run.
   */
  initialOpenLatestRunTimeline?: boolean;
}

const RUN_LOGS_LIMIT = 50;

function runLogsErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: unknown; status?: number } }).response;
    const d = resp?.data;
    if (typeof d === 'string' && d.trim()) return d.trim();
    if (d && typeof d === 'object') {
      const o = d as Record<string, unknown>;
      const m = o.message ?? o.title ?? o.detail ?? o.error;
      if (typeof m === 'string' && m.trim()) return m.trim();
    }
    if (resp?.status != null) return `Could not load run logs (HTTP ${resp.status}).`;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Could not load run logs.';
}

function timelineErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: unknown; status?: number } }).response;
    const d = resp?.data;
    if (typeof d === 'string' && d.trim()) return d.trim();
    if (d && typeof d === 'object') {
      const o = d as Record<string, unknown>;
      const m = o.message ?? o.title ?? o.detail ?? o.error;
      if (typeof m === 'string' && m.trim()) return m.trim();
    }
    if (resp?.status != null) return `Could not load timeline (HTTP ${resp.status}).`;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Could not load timeline.';
}

function isAbortError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const o = err as { code?: string; name?: string };
  return o.code === 'ERR_CANCELED' || o.name === 'CanceledError' || o.name === 'AbortError';
}

function formatLogTime(isoOrText: string): string {
  const s = isoOrText.trim();
  if (!s) return '—';
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(d);
  }
  return s;
}

type RunLogApiItem = {
  runId?: string;
  status?: string;
  summary?: string;
  occurredAtUtc?: string;
  durationSeconds?: number;
  timelineRelativePath?: string;
};

function mapRunStatusToRowStatus(statusRaw: string): LogRow['status'] {
  const sl = statusRaw.trim().toLowerCase();
  if (sl.includes('fail') || sl.includes('error') || sl === 'faulted' || sl === 'cancelled' || sl === 'canceled') {
    return 'Failed';
  }
  if (sl === 'running' || sl === 'in_progress' || sl === 'pending' || sl === 'started') {
    return 'Running';
  }
  return 'Completed';
}

function sortLogRowsNewestFirst(rows: LogRow[]): LogRow[] {
  return [...rows].sort((a, b) => {
    const ta = a.occurredAtUtc ? Date.parse(a.occurredAtUtc) : NaN;
    const tb = b.occurredAtUtc ? Date.parse(b.occurredAtUtc) : NaN;
    const fa = Number.isFinite(ta);
    const fb = Number.isFinite(tb);
    if (fa !== fb) return fb ? 1 : -1;
    if (!fa) return 0;
    return tb - ta;
  });
}

function mapRunLogsApiToRows(raw: unknown): LogRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item, index) => {
    if (typeof item !== 'object' || item === null) {
      return {
        id: String(index),
        time: '—',
        status: 'Completed' as const,
        detail: String(item),
      };
    }
    const r = item as RunLogApiItem;
    const id = String(r.runId ?? index);
    const occurredAtUtc = typeof r.occurredAtUtc === 'string' && r.occurredAtUtc.trim() ? r.occurredAtUtc.trim() : undefined;
    const time = occurredAtUtc ? formatLogTime(occurredAtUtc) : '—';
    const status = mapRunStatusToRowStatus(String(r.status ?? 'Completed'));
    const summary = (r.summary ?? '').trim();
    const dur =
      typeof r.durationSeconds === 'number' && Number.isFinite(r.durationSeconds)
        ? ` · ${r.durationSeconds}s`
        : '';
    const detail = summary ? `${summary}${dur}` : dur.trim() ? dur.replace(/^ · /, '') : '—';
    return {
      id,
      time,
      status,
      detail,
      occurredAtUtc,
      timelineRelativePath:
        typeof r.timelineRelativePath === 'string' && r.timelineRelativePath.trim()
          ? r.timelineRelativePath.trim()
          : undefined,
      durationSeconds: typeof r.durationSeconds === 'number' ? r.durationSeconds : undefined,
    };
  });
}

type TimelineApiItem = {
  title?: string;
  outcome?: string;
  occurredAtUtc?: string;
  inputSummary?: string;
  outputSummary?: string;
  nodeId?: string;
  categoryType?: string | null;
  categoryId?: string | null;
  categoryDetailRelativePath?: string | null;
};

function mapOutcomeToTimelineStatus(outcome: string): TimelineEntry['status'] {
  const o = outcome.trim().toLowerCase();
  if (o.includes('fail') || o.includes('error') || o === 'faulted' || o === 'cancelled' || o === 'canceled') {
    return 'Failed';
  }
  if (o === 'running' || o === 'in_progress' || o === 'pending' || o === 'started') {
    return 'Running';
  }
  return 'Success';
}

function inferTimelineStepType(r: TimelineApiItem): TimelineEntry['type'] {
  const nodeId = (r.nodeId ?? '').toLowerCase();
  const title = (r.title ?? '').toLowerCase();
  const cat = (r.categoryType ?? '').toLowerCase();
  if (nodeId.startsWith('trigger') || title.includes('trigger')) return 'start';
  if (cat.includes('llm')) return 'agent';
  if (cat.includes('email')) return 'email';
  if (cat.includes('action') || cat.includes('http') || cat.includes('rest')) return 'api';
  if (cat.includes('condition')) return 'condition';
  if (cat.includes('branch')) return 'branch';
  if (cat.includes('data') || cat.includes('load') || cat.includes('db')) return 'load';
  return 'load';
}

function mapTimelineApiToEntries(raw: unknown): TimelineEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item, index) => {
    if (typeof item !== 'object' || item === null) {
      return {
        id: `step-${index}`,
        name: `Step ${index + 1}`,
        type: 'load' as const,
        status: 'Success' as const,
        time: '—',
        input: String(item),
        output: '—',
      };
    }
    const r = item as TimelineApiItem;
    const id = (r.nodeId ?? `step-${index}`).trim() || `step-${index}`;
    const name = (r.title ?? `Step ${index + 1}`).trim() || `Step ${index + 1}`;
    const time = r.occurredAtUtc ? formatLogTime(r.occurredAtUtc) : '—';
    const status = mapOutcomeToTimelineStatus(String(r.outcome ?? 'Success'));
    const input = (r.inputSummary ?? '—').trim() || '—';
    const outputBase = (r.outputSummary ?? '—').trim() || '—';
    const detailPath =
      typeof r.categoryDetailRelativePath === 'string' && r.categoryDetailRelativePath.trim()
        ? r.categoryDetailRelativePath.trim()
        : '';
    const output =
      detailPath && !outputBase.includes(detailPath)
        ? `${outputBase}\n\n[Detail: ${detailPath}]`
        : outputBase;
    return {
      id,
      name,
      type: inferTimelineStepType(r),
      status,
      time,
      input,
      output,
      categoryType: r.categoryType ?? null,
      categoryId: r.categoryId ?? null,
    };
  });
}

const statusIcon = {
  Completed: CheckCircle,
  Failed: AlertTriangle,
  Running: Clock,
};

const LogsModal: React.FC<Props> = ({
  workflowId,
  name,
  onClose,
  initialAutoRefreshList = false,
  initialOpenLatestRunTimeline = false,
}) => {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const [selectedLogEntry, setSelectedLogEntry] = useState<LogRow | null>(null);
  const [timelineEntries, setTimelineEntries] = useState<TimelineEntry[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineBgRefreshing, setTimelineBgRefreshing] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);

  const [logs, setLogs] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [listSubRefreshing, setListSubRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [autoRefreshList, setAutoRefreshList] = useState(
    () => Boolean(initialAutoRefreshList && !initialOpenLatestRunTimeline)
  );
  const [autoRefreshTimeline, setAutoRefreshTimeline] = useState(
    () => Boolean(initialAutoRefreshList && initialOpenLatestRunTimeline)
  );

  const autoRefreshListRef = useRef(autoRefreshList);
  autoRefreshListRef.current = autoRefreshList;
  const autoRefreshTimelineRef = useRef(autoRefreshTimeline);
  autoRefreshTimelineRef.current = autoRefreshTimeline;
  const selectedLogEntryRef = useRef(selectedLogEntry);
  selectedLogEntryRef.current = selectedLogEntry;

  const didOpenLatestRunRef = useRef(false);

  const loadLogs = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = opts?.silent ?? false;
      if (!silent) {
        setLoadError(null);
        setLoading(true);
      } else {
        setListSubRefreshing(true);
      }
      const base = resolveWorkflowRunServiceBaseUrl();
      if (!base) {
        setLogs([]);
        setLoadError(
          'Missing REACT_APP_API_URL_workflow_run_logs. Add it to your environment (see .env.example), then restart the dev server.'
        );
        if (!silent) setLoading(false);
        else setListSubRefreshing(false);
        return;
      }
      try {
        const wid = encodeURIComponent(workflowId.trim());
        const path = `/workflows/${wid}/run-logs`;
        const { data } = await apiClient.get<unknown>(path, {
          baseURL: base,
          params: { limit: RUN_LOGS_LIMIT },
        });
        setLogs(sortLogRowsNewestFirst(mapRunLogsApiToRows(data)));
        if (silent) setLoadError(null);
      } catch (e) {
        setLogs([]);
        setLoadError(runLogsErrorMessage(e));
      } finally {
        if (!silent) setLoading(false);
        else setListSubRefreshing(false);
      }
    },
    [workflowId]
  );

  const loadLogsRef = useRef(loadLogs);
  loadLogsRef.current = loadLogs;

  useEffect(() => {
    setSelectedLogEntry(null);
    didOpenLatestRunRef.current = false;
  }, [workflowId]);

  useEffect(() => {
    void loadLogsRef.current();
  }, [workflowId]);

  useEffect(() => {
    if (!autoRefreshList || selectedLogEntry) return;
    const id = window.setInterval(() => {
      if (!autoRefreshListRef.current) return;
      void loadLogsRef.current({ silent: true });
    }, WORKFLOW_LOG_AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [autoRefreshList, selectedLogEntry]);

  useEffect(() => {
    if (!initialOpenLatestRunTimeline || didOpenLatestRunRef.current) return;
    if (loading || loadError) return;
    if (logs.length === 0) return;
    didOpenLatestRunRef.current = true;
    setSelectedLogEntry(logs[0]);
  }, [initialOpenLatestRunTimeline, loading, loadError, logs]);

  const fetchTimelineForRun = useCallback(
    async (run: LogRow, signal?: AbortSignal): Promise<TimelineEntry[]> => {
      const base = resolveWorkflowRunServiceBaseUrl();
      if (!base) {
        throw new Error(
          'Missing REACT_APP_API_URL_workflow_run_logs. Add it to your environment (see .env.example), then restart the dev server.'
        );
      }
      const wid = encodeURIComponent(workflowId.trim());
      const rid = encodeURIComponent(run.id.trim());
      const path = `/workflows/${wid}/run-logs/${rid}/timeline`;
      const { data } = await apiClient.get<unknown>(path, {
        baseURL: base,
        signal,
      });
      return mapTimelineApiToEntries(data);
    },
    [workflowId]
  );

  const runTimelineFetch = useCallback(
    async (run: LogRow, opts: { withSkeleton: boolean; signal?: AbortSignal }) => {
      const { withSkeleton, signal } = opts;
      if (withSkeleton) {
        setTimelineLoading(true);
        setTimelineEntries([]);
      } else {
        setTimelineBgRefreshing(true);
      }
      setTimelineError(null);
      try {
        const entries = await fetchTimelineForRun(run, signal);
        setTimelineEntries(entries);
      } catch (err: unknown) {
        if (signal?.aborted || isAbortError(err)) return;
        setTimelineEntries([]);
        setTimelineError(timelineErrorMessage(err));
      } finally {
        if (withSkeleton) setTimelineLoading(false);
        else setTimelineBgRefreshing(false);
      }
    },
    [fetchTimelineForRun]
  );

  const runTimelineFetchRef = useRef(runTimelineFetch);
  runTimelineFetchRef.current = runTimelineFetch;

  useEffect(() => {
    if (!selectedLogEntry) {
      setTimelineEntries([]);
      setTimelineError(null);
      setTimelineLoading(false);
      setTimelineBgRefreshing(false);
      return;
    }
    const ac = new AbortController();
    void runTimelineFetch(selectedLogEntry, { withSkeleton: true, signal: ac.signal });
    return () => ac.abort();
  }, [selectedLogEntry, runTimelineFetch]);

  useEffect(() => {
    if (!autoRefreshTimeline || !selectedLogEntry) return;
    const id = window.setInterval(() => {
      if (!autoRefreshTimelineRef.current) return;
      const run = selectedLogEntryRef.current;
      if (!run) return;
      void runTimelineFetchRef.current(run, { withSkeleton: false });
    }, WORKFLOW_LOG_AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [autoRefreshTimeline, selectedLogEntry]);

  const listToolbarRefreshing = loading || listSubRefreshing;
  const timelineToolbarRefreshing = timelineLoading || timelineBgRefreshing;

  const statusText = {
    Completed: isLight ? 'text-emerald-700' : 'text-emerald-500',
    Failed: isLight ? 'text-rose-600' : 'text-rose-400',
    Running: isLight ? 'text-amber-700' : 'text-amber-400',
  } as const;

  return (
    <div
      className={`fixed inset-0 z-[100] flex justify-center px-4 pb-6 pt-14 backdrop-blur-sm sm:pt-16 ${
        isLight ? 'bg-slate-900/35' : 'bg-slate-950/80'
      }`}
    >
      <div
        className={`flex w-full max-w-3xl flex-col overflow-hidden rounded-xl border shadow-xl ${
          isLight
            ? 'border-slate-200 bg-white shadow-slate-900/10 ring-1 ring-slate-900/[0.04]'
            : 'border-slate-700/90 bg-slate-900 shadow-black/40'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="workflow-logs-title"
      >
        <header
          className={`shrink-0 border-b px-5 py-4 sm:px-6 ${
            isLight ? 'border-slate-200 bg-slate-50' : 'border-slate-800 bg-slate-900'
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                {selectedLogEntry ? 'Timeline' : 'Runs'}
              </p>
              <h2
                id="workflow-logs-title"
                className={`mt-0.5 truncate text-lg font-semibold tracking-tight ${
                  isLight ? 'text-slate-900' : 'text-slate-50'
                }`}
              >
                {name}
              </h2>
              <p
                className={`mt-1 truncate text-xs ${isLight ? 'text-slate-600' : 'text-slate-500'}`}
                title={workflowId}
              >
                {workflowId}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition ${
                isLight
                  ? 'border-slate-300 text-slate-500 hover:border-slate-400 hover:bg-slate-100 hover:text-slate-800'
                  : 'border-slate-600/90 text-slate-400 hover:border-slate-500 hover:bg-slate-800 hover:text-slate-100'
              }`}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div
          className={`min-h-0 flex-1 overflow-y-auto px-5 py-5 text-sm sm:px-6 sm:py-6 ${
            isLight ? 'bg-white text-slate-800' : 'bg-slate-900 text-slate-100'
          }`}
        >
          {selectedLogEntry ? (
            <>
              <button
                type="button"
                onClick={() => setSelectedLogEntry(null)}
                className={`mb-4 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition ${
                  isLight
                    ? 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
                }`}
              >
                <ChevronRight className="h-4 w-4 rotate-180" aria-hidden />
                Back to run list
              </button>
              <div
                className={`mb-4 rounded-lg border px-4 py-3 ${
                  isLight ? 'border-slate-200 bg-slate-50' : 'border-slate-700/80 bg-slate-950/40'
                }`}
              >
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Selected run</p>
                <p className={`mt-1 truncate font-mono text-xs ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>
                  {selectedLogEntry.id}
                </p>
                {timelineBgRefreshing && !timelineLoading ? (
                  <p className={`mt-2 text-[11px] ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>Updating…</p>
                ) : null}
              </div>
              {timelineLoading ? (
                <div className={`flex flex-col items-center justify-center gap-3 py-16 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                  <Loader2 className={`h-8 w-8 animate-spin ${isLight ? 'text-slate-400' : 'text-slate-500'}`} aria-hidden />
                  <p className={`text-sm font-medium ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>Loading timeline…</p>
                </div>
              ) : timelineError ? (
                <div
                  className={`rounded-lg border px-4 py-4 text-sm ${
                    isLight
                      ? 'border-rose-200 bg-rose-50 text-rose-900'
                      : 'border-rose-900/40 bg-rose-950/25 text-rose-100'
                  }`}
                >
                  <p className={`font-semibold ${isLight ? 'text-rose-800' : 'text-rose-200'}`}>Could not load timeline</p>
                  <p className={isLight ? 'mt-1 text-rose-800/90' : 'mt-1 text-rose-100/90'}>{timelineError}</p>
                  <button
                    type="button"
                    onClick={() => selectedLogEntry && void runTimelineFetch(selectedLogEntry, { withSkeleton: true })}
                    className={`mt-4 text-xs font-semibold hover:underline ${
                      isLight ? 'text-blue-700 hover:text-blue-800' : 'text-blue-400 hover:text-blue-300'
                    }`}
                  >
                    Try again
                  </button>
                </div>
              ) : timelineEntries.length === 0 ? (
                <p className={`py-12 text-center text-sm ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>
                  No timeline steps for this run.
                </p>
              ) : (
                <div
                  className={`rounded-lg border p-1 ${isLight ? 'border-slate-200 bg-slate-50' : 'border-slate-800 bg-slate-950/50'}`}
                >
                  <WorkflowTimeline
                    entries={timelineEntries}
                    workflowId={workflowId}
                    runId={selectedLogEntry.id}
                  />
                </div>
              )}
            </>
          ) : loading ? (
            <div className={`flex flex-col items-center justify-center gap-3 py-16 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
              <Loader2 className={`h-8 w-8 animate-spin ${isLight ? 'text-slate-400' : 'text-slate-500'}`} aria-hidden />
              <p className={`text-sm font-medium ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>Loading runs…</p>
            </div>
          ) : loadError ? (
            <div
              className={`rounded-lg border px-4 py-4 text-sm ${
                isLight
                  ? 'border-rose-200 bg-rose-50 text-rose-900'
                  : 'border-rose-900/40 bg-rose-950/25 text-rose-100'
              }`}
            >
              <p className={`font-semibold ${isLight ? 'text-rose-800' : 'text-rose-200'}`}>Could not load runs</p>
              <p className={isLight ? 'mt-1 text-rose-800/90' : 'mt-1 text-rose-100/90'}>{loadError}</p>
              <button
                type="button"
                onClick={() => void loadLogs({ silent: false })}
                className={`mt-4 text-xs font-semibold hover:underline ${
                  isLight ? 'text-blue-700 hover:text-blue-800' : 'text-blue-400 hover:text-blue-300'
                }`}
              >
                Try again
              </button>
            </div>
          ) : logs.length === 0 ? (
            <p className={`py-12 text-center text-sm ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>
              No log entries for this workflow yet.
            </p>
          ) : (
            <ul className="space-y-3">
              {listSubRefreshing ? (
                <li
                  className={`rounded-lg border px-3 py-2 text-center text-[11px] font-medium ${
                    isLight
                      ? 'border-slate-200 bg-slate-100 text-slate-600'
                      : 'border-slate-600/60 bg-slate-800/40 text-slate-400'
                  }`}
                >
                  Refreshing…
                </li>
              ) : null}
              {logs.map((log) => {
                const Icon = statusIcon[log.status];
                return (
                  <li
                    key={log.id}
                    className={`group rounded-lg border p-4 transition ${
                      isLight
                        ? 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100/80'
                        : 'border-slate-700/80 bg-slate-950/30 hover:border-slate-600 hover:bg-slate-900/50'
                    }`}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Icon className={`h-4 w-4 shrink-0 ${statusText[log.status]}`} aria-hidden />
                          <span className={`text-sm font-semibold ${statusText[log.status]}`}>{log.status}</span>
                          <span className={`text-[11px] ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>{log.time || '—'}</span>
                        </div>
                        <p className={`mt-2 text-sm leading-relaxed ${isLight ? 'text-slate-700' : 'text-slate-200'}`}>
                          {log.detail}
                        </p>
                        <p className={`mt-2 truncate font-mono text-[10px] ${isLight ? 'text-slate-500' : 'text-slate-600'}`}>
                          {log.id}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedLogEntry(log)}
                        className={`shrink-0 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                          isLight
                            ? 'border-slate-300 bg-white text-slate-800 hover:border-blue-500 hover:bg-slate-50'
                            : 'border-slate-600 bg-slate-800/80 text-slate-100 hover:border-blue-500/50 hover:bg-slate-700'
                        }`}
                      >
                        View timeline
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer
          className={`flex shrink-0 flex-col gap-3 border-t px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 ${
            isLight ? 'border-slate-200 bg-slate-100' : 'border-slate-800 bg-slate-950'
          }`}
        >
          <p className={`text-center text-[11px] sm:text-left ${isLight ? 'text-slate-500' : 'text-slate-600'}`}>
            {selectedLogEntry ? 'Refresh updates the step list for this run.' : 'Showing recent runs for this workflow.'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
            {selectedLogEntry ? (
              <WorkflowLogModalToolbar
                onManualRefresh={() => {
                  if (selectedLogEntry) void runTimelineFetch(selectedLogEntry, { withSkeleton: false });
                }}
                isRefreshing={timelineToolbarRefreshing}
                autoRefreshEnabled={autoRefreshTimeline}
                onAutoRefreshEnabledChange={setAutoRefreshTimeline}
                disabled={!resolveWorkflowRunServiceBaseUrl()}
                isLight={isLight}
              />
            ) : (
              <WorkflowLogModalToolbar
                onManualRefresh={() => void loadLogs({ silent: false })}
                isRefreshing={listToolbarRefreshing}
                autoRefreshEnabled={autoRefreshList}
                onAutoRefreshEnabledChange={setAutoRefreshList}
                disabled={!resolveWorkflowRunServiceBaseUrl()}
                isLight={isLight}
              />
            )}
            <button
              type="button"
              onClick={onClose}
              className={`rounded-lg px-4 py-2 text-xs font-semibold transition ${
                isLight
                  ? 'bg-slate-900 text-white hover:bg-slate-800'
                  : 'bg-slate-100 text-slate-900 hover:bg-white'
              }`}
            >
              Close
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LogsModal;
